import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AxiosError } from 'axios';
import {
  getDashboardSummary,
  type DashboardSummary,
  type DashboardWindow,
  type TopHwid,
  type TopIp,
  type TopIncident,
} from '../../services/dashboard';
import { getBackendHealth, type BackendHealth } from '../../services/health';
import { AttackCategoryBadge } from '../../components/AttackCategoryBadge';
import { SeverityIndicator } from '../../components/SeverityIndicator';
import { Sparkline } from '../../components/Sparkline';
import { RelativeTime } from '../../components/RelativeTime';
import { TopNSelector, TimeRangeSelector } from '../../components/DashboardControls';

interface FriendlyError {
  title: string;
  detail?: string;
  hint?: string;
}

function describeDashboardError(err: unknown): FriendlyError {
  if (err instanceof AxiosError) {
    const status = err.response?.status;
    if (status === 401) {
      return { title: 'Sessão expirou', hint: 'Faça login novamente.' };
    }
    if (status === 403) {
      return { title: 'Sem permissão', hint: 'Sua conta não tem privilégio SUPER_ADMIN.' };
    }
    if (status === 404) {
      return {
        title: 'Backend não encontrou o endpoint',
        detail: 'Pode ser uma versão antiga do painel ou backend desatualizado.',
        hint: 'Force-recarregue (Ctrl+Shift+R). Se persistir, conferir deploy.',
      };
    }
    if (status === 503) {
      return { title: 'Backend indisponível', hint: 'API em manutenção ou DB inacessível.' };
    }
    if (status && status >= 500) {
      return { title: `Erro do backend (HTTP ${status})`, detail: err.response?.statusText };
    }
    if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
      return { title: 'Timeout', hint: 'Backend demorou demais. Tentar de novo.' };
    }
    if (err.code === 'ERR_NETWORK') {
      return { title: 'Backend inacessível', hint: 'Sem conexão ou backend offline.' };
    }
    return { title: `Erro de rede (${err.code ?? 'desconhecido'})`, detail: err.message };
  }
  return { title: 'Erro inesperado', detail: err instanceof Error ? err.message : String(err) };
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function HealthDot({ ok }: { ok: boolean }) {
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-red-500'}`}
      aria-label={ok ? 'ok' : 'erro'}
    />
  );
}

function BackendHealthCard({ health, error }: {
  health: BackendHealth | null;
  error: string | null;
}) {
  if (error && !health) {
    return (
      <div className="bg-gray-900 border border-red-800 rounded-lg p-4">
        <div className="text-xs uppercase text-gray-500 tracking-wide flex items-center gap-2">
          <HealthDot ok={false} />
          Backend
        </div>
        <div className="text-sm text-red-300 mt-2">erro: {error}</div>
      </div>
    );
  }
  if (!health) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <div className="text-xs uppercase text-gray-500 tracking-wide">Backend</div>
        <div className="text-sm text-gray-500 mt-2">carregando…</div>
      </div>
    );
  }

  const dbOk = health.db === 'ready';
  const statusOk = health.status === 'ok';

  return (
    <div
      className={`bg-gray-900 border rounded-lg p-4 ${
        statusOk ? 'border-gray-800' : 'border-amber-800'
      }`}
    >
      <div className="text-xs uppercase text-gray-500 tracking-wide flex items-center gap-2">
        <HealthDot ok={statusOk} />
        Backend ({health.service})
      </div>
      <div className="grid grid-cols-2 gap-y-2 gap-x-3 mt-2 text-sm">
        <div className="text-gray-500">Status</div>
        <div className={statusOk ? 'text-emerald-400' : 'text-amber-400'}>
          {health.status}
        </div>
        <div className="text-gray-500">DB</div>
        <div className={dbOk ? 'text-emerald-400' : 'text-red-400'}>
          {health.db}
        </div>
        <div className="text-gray-500">R2</div>
        <div className={health.r2_configured ? 'text-emerald-400' : 'text-gray-500'}>
          {health.r2_configured
            ? `wired${health.r2_bucket ? ` (${health.r2_bucket})` : ''}`
            : 'não configurado'}
        </div>
        <div className="text-gray-500">Uptime</div>
        <div className="text-gray-200 font-mono">{formatUptime(health.uptime_seconds)}</div>
        <div className="text-gray-500">Build</div>
        <div className="text-gray-200 font-mono text-xs">{health.version_build_time}</div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <div className="text-xs uppercase text-gray-500 tracking-wide">{label}</div>
      <div className="text-3xl font-bold text-gray-100 mt-1">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function EventTypeBar({ event_type, count, max }: { event_type: string; count: number; max: number }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="text-xs text-gray-400 w-48 truncate" title={event_type}>{event_type}</div>
      <div className="flex-1 h-3 bg-gray-800 rounded overflow-hidden">
        <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs text-gray-400 w-12 text-right tabular-nums">{count}</div>
    </div>
  );
}

// Composite score: count * max_severity / 100. Industry pattern (SIEM weighted analysis).
function compositeScore(count: number, severity: number): number {
  return Math.round(count * severity / 100 * 10) / 10;  // 1 decimal
}

function TopIncidentsList({ items }: { items: TopIncident[] }) {
  if (items.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        Nenhum incident no período.
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {items.map((inc) => (
        <li key={inc.uuid} className="flex items-start gap-2">
          <SeverityIndicator severity={inc.severity_max} showLabel={false} />
          <div className="flex-1 min-w-0">
            <Link
              to={`/incidents/${inc.uuid}`}
              className="text-sm text-gray-200 hover:text-blue-400 line-clamp-1"
              title={inc.summary || inc.attack_category}
            >
              {inc.summary || inc.attack_category}
            </Link>
            <div className="flex items-center gap-2 mt-0.5">
              <AttackCategoryBadge category={inc.attack_category} />
              <RelativeTime ts={inc.created_at} />
              {inc.hwid && (
                <Link
                  to={`/investigations/hwid/${inc.hwid}`}
                  className="text-xs text-gray-500 hover:text-blue-400 truncate max-w-[8rem]"
                  title={inc.hwid}
                >
                  {inc.hwid}
                </Link>
              )}
            </div>
          </div>
          <span className="text-xs text-gray-500 tabular-nums">{inc.severity_max}</span>
        </li>
      ))}
    </ul>
  );
}

function TopHwidsList({ items, windowLabel }: { items: TopHwid[]; windowLabel: string }) {
  if (items.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        Nenhum HWID flagged no período ({windowLabel}).
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {items.map((h) => {
        const score = compositeScore(h.incident_count, h.max_severity);
        return (
          <li key={h.hwid} className="flex items-center gap-2">
            <SeverityIndicator severity={h.max_severity} showLabel={false} />
            <Link
              to={`/investigations/hwid/${h.hwid}`}
              className="flex-1 text-sm text-gray-200 hover:text-blue-400 font-mono truncate"
              title={h.hwid}
            >
              {h.hwid}
            </Link>
            <span
              className="text-orange-300"
              title={`Sparkline 7d (max ${Math.max(...h.sparkline_7d)})`}
            >
              <Sparkline data={h.sparkline_7d} />
            </span>
            <span
              className="text-xs text-gray-400 tabular-nums w-12 text-right"
              title={`total incidents = ${h.incident_count}, hard subset = ${h.hard_incident_count}, score = ${score}`}
            >
              {h.incident_count}
            </span>
            <RelativeTime ts={h.last_seen_at} />
          </li>
        );
      })}
    </ul>
  );
}

function TopIpsList({ items, windowLabel }: { items: TopIp[]; windowLabel: string }) {
  if (items.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        Nenhum IP flagged no período ({windowLabel}).
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {items.map((ip) => {
        const score = compositeScore(ip.incident_count, ip.max_severity);
        return (
          <li key={ip.source_ip} className="flex items-center gap-2">
            <SeverityIndicator severity={ip.max_severity} showLabel={false} />
            <Link
              to={`/investigations/ip/${ip.source_ip}`}
              className="flex-1 text-sm text-gray-200 hover:text-blue-400 font-mono truncate"
              title={ip.source_ip}
            >
              {ip.source_ip}
            </Link>
            <span className="text-blue-300">
              <Sparkline data={ip.sparkline_7d} />
            </span>
            <span
              className="text-xs text-gray-400 tabular-nums w-12 text-right"
              title={`total incidents = ${ip.incident_count}, hard subset = ${ip.hard_incident_count}, distinct HWIDs = ${ip.distinct_hwids_count}, score = ${score}`}
            >
              {ip.incident_count}
            </span>
            <RelativeTime ts={ip.last_seen_at} />
          </li>
        );
      })}
    </ul>
  );
}

export function Dashboard() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<FriendlyError | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [health, setHealth] = useState<BackendHealth | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [window, setWindow] = useState<DashboardWindow>('7d');
  const [topN, setTopN] = useState<number>(10);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getDashboardSummary({ window, limit: topN })
      .then((r) => {
        if (!cancelled) setSummary(r);
      })
      .catch((e) => {
        if (!cancelled) setError(describeDashboardError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshTick, window, topN]);

  useEffect(() => {
    let cancelled = false;
    setHealthError(null);
    getBackendHealth()
      .then((h) => { if (!cancelled) setHealth(h); })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e);
        if (!cancelled) setHealthError(msg || 'erro health');
      });
    return () => { cancelled = true; };
  }, [refreshTick]);

  useEffect(() => {
    const id = setInterval(() => setRefreshTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  if (loading && !summary) {
    return <div className="text-gray-500">Carregando dashboard…</div>;
  }
  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-800 text-red-300 rounded p-4 space-y-1">
        <div className="font-semibold">{error.title}</div>
        {error.detail && <div className="text-sm text-red-300/80">{error.detail}</div>}
        {error.hint && <div className="text-xs text-red-300/60">{error.hint}</div>}
        <button
          onClick={() => setRefreshTick((t) => t + 1)}
          className="mt-2 text-xs px-3 py-1 bg-red-800/40 hover:bg-red-800/60 text-red-100 rounded border border-red-700"
        >
          ↻ Tentar de novo
        </button>
      </div>
    );
  }
  if (!summary) return null;

  const maxEventCount = summary.events_24h_by_type.reduce(
    (acc, e) => Math.max(acc, e.count),
    1,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-gray-100">Dashboard</h1>
        <div className="flex items-center gap-3">
          <TimeRangeSelector value={window} onChange={setWindow} />
          <TopNSelector value={topN} onChange={setTopN} />
          <button
            onClick={() => setRefreshTick((t) => t + 1)}
            className="text-xs px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded border border-gray-700"
            aria-label="Atualizar"
          >
            ↻ Atualizar
          </button>
        </div>
      </div>

      <BackendHealthCard health={health} error={healthError} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Sessões ativas"
          value={summary.sessions_active}
          sub="heartbeat < 5 min"
        />
        <StatCard
          label="Eventos (24h)"
          value={summary.events_24h_total}
          sub={`${summary.events_24h_by_type.length} tipos distintos`}
        />
        <StatCard
          label="Kill switches"
          value={summary.kill_switches_active}
          sub="ativos no momento"
        />
        <StatCard
          label="Ruleset ativo"
          value={summary.ruleset.present ? `${summary.ruleset.rules_count} rules` : '—'}
          sub={summary.ruleset.present ? `${summary.ruleset.version}` : 'nenhum publicado'}
        />
      </div>

      {/* Top offenders trio — 2026-05-23 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h2 className="text-sm uppercase text-gray-400 tracking-wide mb-3">
            Top incidents ({summary.window})
          </h2>
          <TopIncidentsList items={summary.top_incidents} />
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h2 className="text-sm uppercase text-gray-400 tracking-wide mb-3">
            Top HWIDs flagged ({summary.window})
            <span className="ml-2 text-xs text-gray-600">sparkline 7d</span>
          </h2>
          <TopHwidsList items={summary.top_hwids} windowLabel={summary.window} />
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h2 className="text-sm uppercase text-gray-400 tracking-wide mb-3">
            Top IPs flagged ({summary.window})
            <span className="ml-2 text-xs text-gray-600">sparkline 7d</span>
          </h2>
          <TopIpsList items={summary.top_ips} windowLabel={summary.window} />
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <h2 className="text-sm uppercase text-gray-400 tracking-wide mb-3">
          Eventos por tipo (últimas 24h)
        </h2>
        {summary.events_24h_by_type.length === 0 ? (
          <div className="text-sm text-gray-500 italic">Nenhum evento nas últimas 24h.</div>
        ) : (
          <div className="space-y-1">
            {summary.events_24h_by_type.map((e) => (
              <EventTypeBar
                key={e.event_type}
                event_type={e.event_type}
                count={e.count}
                max={maxEventCount}
              />
            ))}
          </div>
        )}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <h2 className="text-sm uppercase text-gray-400 tracking-wide mb-3">Ruleset ativo</h2>
        {summary.ruleset.present ? (
          <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
            <dt className="text-gray-500">Versão</dt>
            <dd className="text-gray-200 font-mono">{summary.ruleset.version}</dd>
            <dt className="text-gray-500">Key ID</dt>
            <dd className="text-gray-200 font-mono">{summary.ruleset.key_id}</dd>
            <dt className="text-gray-500">Total de rules</dt>
            <dd className="text-gray-200">{summary.ruleset.rules_count}</dd>
          </dl>
        ) : (
          <div className="text-sm text-gray-500 italic">
            Nenhum ruleset publicado. SDK roda em fallback hardcoded.
          </div>
        )}
      </div>

      <div className="text-xs text-gray-600 text-right">
        Auto-refresh a cada 30s. Última atualização:{' '}
        {new Date().toLocaleTimeString()}
      </div>
    </div>
  );
}
